import React, { useState, useEffect } from 'react';
import { FaSearch, FaSpinner, FaStar, FaRegStar, FaEdit, FaTrash, FaCheck, FaTimes } from 'react-icons/fa';
import { useUser } from '../../../../UserContext';
import EmblemCreator from './EmblemCreator';
import BannerCreator from './BannerCreator';

export default function CustomAssetsModal({ isOpen, onClose, modalType, onSelect }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(50);
  const [activeTab, setActiveTab] = useState(0); // 0:All, 1:My, 2:Fav, 3:Create
  const { user } = useUser();

  const [loadedType, setLoadedType] = useState(null);

  // States for inline renaming
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [editNameValue, setEditNameValue] = useState('');

  const getModalConfig = (type) => {
    switch (type) {
      case 'customClass': return { title: 'Custom Classes', placeholder: 'Search community classes...', apiType: 0 };
      case 'customDualClass': return { title: 'Custom Dual Classes', placeholder: 'Search community classes...', apiType: 0 };
      case 'customEmblem': return { title: 'Custom Emblems', placeholder: 'Search community emblems...', apiType: 1 };
      case 'customBanner': return { title: 'Custom Banners', placeholder: 'Search community banners...', apiType: 2 };
      case 'customCardType': return { title: 'Custom Card Types', placeholder: 'Search custom card types...', apiType: 4 }; // ADDED: New card type support
      default: return { title: 'Search Assets', placeholder: 'Search...', apiType: 0 };
    }
  };

  const config = getModalConfig(modalType);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setVisibleCount(50);
      setActiveTab(0);
      setEditingAssetId(null);
      performSearch('', 0, modalType, config.apiType);
    } else {
      setAssets([]);
      setLoadedType(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, modalType]);

  const performSearch = async (query, tab = activeTab, currentModalType = modalType, apiType = config.apiType) => {
    if (tab === 3) return; // Don't search if on "Create" tab
    
    setLoading(true);
    try {
      const res = await fetch(`/ajax/customhs.php?customtype=${apiType}&type=${tab}&search=${encodeURIComponent(query)}`);
      const data = await res.json();
      setAssets(data.emblems || []);
      setLoadedType(currentModalType); 
    } catch (err) {
      console.error("Custom Asset Search Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setEditingAssetId(null);
    if (tabId !== 3) {
      setVisibleCount(50);
      performSearch(searchQuery, tabId);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      performSearch(searchQuery);
    }
  };

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 150) {
      setVisibleCount((prev) => Math.min(prev + 50, assets.length));
    }
  };

  const handleFavoriteToggle = async (e, item) => {
    e.stopPropagation();
    const targetId = item.emblemNumId || item.emblemId;
    
    try {
      setAssets((prevAssets) =>
        prevAssets.map((asset) => {
          if (asset.emblemId === item.emblemId) {
            return { ...asset, state: asset.state === '1' ? '0' : '1' };
          }
          return asset;
        })
      );
      const res = await fetch(`/ajax/favorite.php?id=${targetId}`);
      if (!res.ok) throw new Error('Favorite request failed');
    } catch (err) {
      console.error("Error updating favorite status:", err);
      // Revert state
      setAssets((prevAssets) =>
        prevAssets.map((asset) => {
          if (asset.emblemId === item.emblemId) {
            return { ...asset, state: asset.state === '1' ? '0' : '1' };
          }
          return asset;
        })
      );
    }
  };

  // Handle renaming of user assets
  const handleRename = async (item) => {
    const trimmedName = editNameValue.trim();
    if (!trimmedName || trimmedName === item.emblemName) {
      setEditingAssetId(null);
      return;
    }

    try {
      const formData = new FormData();
      formData.append('elementid', item.emblemId);
      formData.append('action', 'rename');
      formData.append('name', trimmedName);
      formData.append('apiType', config.apiType);

      const res = await fetch('/api/customhs_manage.php', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();

      if (data.success) {
        setAssets((prev) =>
          prev.map((asset) =>
            asset.emblemId === item.emblemId ? { ...asset, emblemName: trimmedName } : asset
          )
        );
      } else {
        alert(data.error || 'Failed to rename asset.');
      }
    } catch (err) {
      console.error("Error renaming asset:", err);
    } finally {
      setEditingAssetId(null);
    }
  };

  // Handle deleting of user assets
  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to permanently delete "${item.emblemName}"? This will also remove the asset files.`)) {
      return;
    }

    try {
      const formData = new FormData();
      formData.append('elementid', item.emblemId);
      formData.append('action', 'delete');
      formData.append('apiType', config.apiType);

      const res = await fetch('/api/customhs_manage.php', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();

      if (data.success) {
        setAssets((prev) => prev.filter((asset) => asset.emblemId !== item.emblemId));
      } else {
        alert(data.error || 'Failed to delete asset.');
      }
    } catch (err) {
      console.error("Error deleting asset:", err);
    }
  };

  const getImagePath = (item) => {
    if (modalType === 'customClass' || modalType === 'customDualClass') return `/assets/classes/${item.emblemId}/minion.png`;
    if (modalType === 'customEmblem') return `/assets/emblems/preview/${item.emblemId}.webp`;
    if (modalType === 'customBanner') return `/assets/banners/${item.emblemId}.png`;
    if (modalType === 'customCardType') return `/assets/templates/${item.emblemId}/preview.webp`; // ADDED: Path for custom card type previews
    return '';
  };

  if (!isOpen) return null;

  const visibleAssets = assets.slice(0, visibleCount);
  const isSafeToRender = loadedType === modalType;
  const showLoading = loading || !isSafeToRender;

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div className="modal fade show d-block" tabIndex="-1" role="dialog" data-bs-theme="dark">
        <div className="modal-dialog modal-dialog-centered modal-lg modal-fullscreen-md-down h-100">
          <div className="custom-modal modal-content bg-dark text-white border-secondary h-100">
            <div className="modal-header border-secondary">
              <h5 className="modal-title brand-text text-warning">{config.title}</h5>
              <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
            </div>

            {user && (
              <div className="px-3 pt-2 bg-black bg-opacity-25 border-bottom border-secondary">
                <ul className="nav nav-tabs border-0 flex-nowrap overflow-auto" style={{ scrollbarWidth: 'none' }}>
                  <li className="nav-item">
                    <button className={`nav-link border-0 text-white bg-transparent text-nowrap ${activeTab === 0 ? 'active border-bottom border-warning text-warning fw-bold' : 'opacity-75'}`} onClick={() => handleTabChange(0)}>
                      All
                    </button>
                  </li>
                  <li className="nav-item">
                    <button className={`nav-link border-0 text-white bg-transparent text-nowrap ${activeTab === 1 ? 'active border-bottom border-warning text-warning fw-bold' : 'opacity-75'}`} onClick={() => handleTabChange(1)}>
                      My Assets
                    </button>
                  </li>
                  <li className="nav-item">
                    <button className={`nav-link border-0 text-white bg-transparent text-nowrap ${activeTab === 2 ? 'active border-bottom border-warning text-warning fw-bold' : 'opacity-75'}`} onClick={() => handleTabChange(2)}>
                      Favorited
                    </button>
                  </li>
                  {/* The Create tab for emblems and banners */}
                  {(modalType === 'customEmblem' || modalType === 'customBanner') && (
                    <li className="nav-item ms-auto">
                      <button className={`nav-link border-0 text-white bg-transparent text-nowrap ${activeTab === 3 ? 'active border-bottom border-warning text-warning fw-bold' : 'opacity-75'}`} onClick={() => handleTabChange(3)}>
                        + Create
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            )}

            <div className="modal-body d-flex flex-column overflow-hidden p-0 position-relative">
              {activeTab === 3 ? (
                modalType === 'customBanner' ? (
                  <BannerCreator onSuccess={() => handleTabChange(1)} />
                ) : (
                  <EmblemCreator onSuccess={() => handleTabChange(1)} />
                )
              ) : (
                <>
                  <div className="p-3 border-bottom border-secondary bg-black bg-opacity-25">
                    <div className="input-group">
                      <input 
                        type="text" 
                        className="form-control bg-black text-white border-secondary" 
                        placeholder={config.placeholder}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                      />
                      <button className="btn btn-outline-warning px-4" type="button" onClick={() => performSearch(searchQuery)}>
                        <FaSearch className="me-2" /> Search
                      </button>
                    </div>
                  </div>

                  <div className="row g-3 p-3 flex-grow-1 m-0 align-content-start" style={{ overflowY: 'auto' }} onScroll={handleScroll}>
                    {showLoading ? (
                      <div className="col-12 text-center text-muted align-self-center py-5 mt-5">
                        <FaSpinner className="fa-spin fa-3x text-warning mb-3 d-block mx-auto" /> 
                        Loading assets...
                      </div>
                    ) : assets.length === 0 ? (
                      <div className="col-12 text-center text-muted align-self-center py-5 mt-5">
                        No results found.
                      </div>
                    ) : (
                      visibleAssets.map((item, index) => (
                        <div key={`${item.emblemId}-${index}`} className={modalType === 'customEmblem' || modalType === 'customCardType' ? 'col-4 col-md-3 text-center mb-2' : 'col-4 col-md-2 text-center mb-2'}>
                          <div className="position-relative d-inline-block w-100 asset-container group">
                            <img 
                              loading="lazy" src={getImagePath(item)} 
                              className="img-fluid custom-result-img rounded transition-transform" 
                              alt={item.emblemName} title={item.emblemName}
                              style={{ cursor: 'pointer' }}
                              onClick={() => onSelect(item.emblemId)}
                            />
                            {user && activeTab !== 1 && (
                              <button
                                type="button"
                                className="btn btn-link p-1 position-absolute"
                                style={{ bottom: '-5px', right: '-2px', zIndex: 10, textShadow: '0 0 4px rgba(0,0,0,0.8)' }}
                                onClick={(e) => handleFavoriteToggle(e, item)}
                              >
                                {item.state === '1' ? <FaStar className="text-warning" size={24} /> : <FaRegStar className="text-warning opacity-75 hover-opacity-100" size={24} />}
                              </button>
                            )}
                          </div>

                          {editingAssetId === item.emblemId ? (
                            <div className="d-flex align-items-center gap-1 mt-1 justify-content-center">
                              <input
                                type="text"
                                className="form-control form-control-sm text-white bg-black border-warning text-center"
                                value={editNameValue}
                                onChange={(e) => setEditNameValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleRename(item);
                                  if (e.key === 'Escape') setEditingAssetId(null);
                                }}
                                autoFocus
                                style={{ fontSize: '0.75rem', height: '24px' }}
                              />
                              <button className="btn btn-sm btn-success p-1 d-flex" onClick={() => handleRename(item)}>
                                <FaCheck size={10} />
                              </button>
                              <button className="btn btn-sm btn-danger p-1 d-flex" onClick={() => setEditingAssetId(null)}>
                                <FaTimes size={10} />
                              </button>
                            </div>
                          ) : (
                            <div className="d-flex flex-column align-items-center mt-1">
                              <div className="small text-truncate text-muted w-100 px-1" style={{ fontSize: '0.75rem' }} title={item.emblemName}>
                                {item.emblemName}
                              </div>
                              {activeTab === 1 && (
                                <div className="d-flex justify-content-center gap-2 mt-1">
                                  <button 
                                    className="btn btn-sm btn-link text-warning p-0 border-0" 
                                    onClick={() => { setEditingAssetId(item.emblemId); setEditNameValue(item.emblemName); }}
                                    title="Rename"
                                  >
                                    <FaEdit size={14} />
                                  </button>
                                  <button 
                                    className="btn btn-sm btn-link text-danger p-0 border-0" 
                                    onClick={() => handleDelete(item)}
                                    title="Delete"
                                  >
                                    <FaTrash size={14} />
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
